// Footer.jsx
import React from 'react';
import { NavLink } from 'react-router-dom';
import { FaFacebookF, FaInstagram, FaYoutube, FaTiktok, FaMapMarkerAlt, FaEnvelope, FaPhoneAlt } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import footerBgSmMd from '../assets/pick/41.jpg';
import footerBgLg from '../assets/pick/42.jpg';

const Footer = () => {
    const { t } = useTranslation();

    return (
        <footer className="relative bg-cover bg-center text-white py-12 sm:py-16 md:py-20 lg:py-24 overflow-hidden">
            {/* Background Images */}
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat lg:hidden"
                style={{ backgroundImage: `url(${footerBgSmMd})` }}
            ></div>
            <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat hidden lg:block"
                style={{ backgroundImage: `url(${footerBgLg})`, backgroundAttachment: 'fixed' }}
            ></div>

            {/* Dark Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/70 to-black/30"></div>

            <div className="container mx-auto px-4 sm:px-6 md:px-10 lg:px-16 relative z-10">
                {/* Main Footer Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 lg:gap-16 text-center lg:text-left">

                    {/* Pick Lanka Links */}
                    <div className="space-y-6">
                        <h5 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#009CE6] mb-6 tracking-tight uppercase font-konkhmer">
                            {t('footer.pickLankaTitle')}
                        </h5>
                        <ul className="space-y-3">
                            {[
                                { to: '/', key: 'home' },
                                { to: '/about-us', key: 'aboutUs' },
                                { to: '/srilanka', key: 'sriLanka' },
                                { to: '/tour', key: 'tourPackages' },
                                { to: '/gallery', key: 'gallery' },
                                { to: '/events', key: 'events' },
                                { to: '/things', key: 'thingsToDo' },
                                { to: '/contact-us', key: 'contactUs' },
                            ].map((link) => (
                                <li key={link.to}>
                                    <NavLink
                                        to={link.to}
                                        className="text-white hover:text-[#96BA33] text-base sm:text-lg font-medium font-redHatDisplay transition-all duration-300 ease-in-out hover:underline hover:underline-offset-8 hover:scale-105 block"
                                    >
                                        {t(`footer.links.${link.key}`)}
                                    </NavLink>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Get in Touch */}
                    <div className="space-y-6">
                        <h5 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#009CE6] mb-6 tracking-tight uppercase font-konkhmer">
                            {t('footer.getInTouch')}
                        </h5>
                        <ul className="space-y-4">
                            <li className="flex items-center justify-center lg:justify-start gap-4">
                                <FaMapMarkerAlt className="w-6 sm:w-7 h-6 sm:h-7 text-[#009CE6] flex-shrink-0" />
                                <span className="text-base sm:text-lg font-medium text-white leading-relaxed font-redHatDisplay">
                                    {t('footer.address')}
                                </span>
                            </li>
                            <li className="flex items-center justify-center lg:justify-start gap-4">
                                <FaEnvelope className="w-6 sm:w-7 h-6 sm:h-7 text-[#009CE6] flex-shrink-0" />
                                <a
                                    href="mailto:info@picklanka.com"
                                    className="text-base sm:text-lg font-medium text-white hover:text-[#96BA33] transition-all duration-300 ease-in-out hover:underline hover:underline-offset-8 hover:scale-105 font-redHatDisplay"
                                >
                                    {t('footer.email')}
                                </a>
                            </li>
                            <li className="flex items-center justify-center lg:justify-start gap-4">
                                <FaPhoneAlt className="w-6 sm:w-7 h-6 sm:h-7 text-[#009CE6] flex-shrink-0" />
                                <a
                                    href="tel:+94770806767"
                                    className="text-base sm:text-lg font-medium text-white hover:text-[#96BA33] transition-all duration-300 ease-in-out hover:underline hover:underline-offset-8 hover:scale-105 font-redHatDisplay"
                                >
                                    {t('footer.phone')}
                                </a>
                            </li>
                        </ul>
                    </div>

                    {/* Stay Connected */}
                    <div className="space-y-6">
                        <h5 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#009CE6] mb-6 tracking-tight uppercase font-konkhmer">
                            {t('footer.stayConnected')}
                        </h5>

                        {/* Newsletter */}
                        <div className="flex justify-center mb-8">
                            <div className="relative w-full max-w-[90%] sm:max-w-md">
                                <input
                                    type="email"
                                    placeholder={t('footer.newsletterPlaceholder')}
                                    className="w-full p-3 sm:p-4 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-[#009CE6] text-black text-base sm:text-lg font-medium bg-white/95 transition-all duration-300 placeholder-gray-500 shadow-sm pr-16"
                                    aria-label={t('footer.newsletterAria')}
                                />
                                <button className="absolute right-0 top-0 h-full px-6 sm:px-8 bg-gradient-to-r from-[#009CE6] to-[#96BA33] text-white rounded-full hover:opacity-90 transition-all duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#009CE6] shadow-lg font-bold">
                                    {t('footer.sendButton')}
                                </button>
                            </div>
                        </div>

                        {/* Social Media Icons */}
                        <div className="flex justify-center gap-6 sm:gap-8">
                            {[
                                { href: 'https://www.facebook.com/TravelwithPicklanka?mibextid=LQQJ4d', icon: FaFacebookF, label: t('footer.social.facebook') },
                                { href: 'https://www.instagram.com/travelwithpicklanka?igsh=cmNwamJ5NW9teHk4&utm_source=qr', icon: FaInstagram, label: t('footer.social.instagram') },
                                { href: 'https://youtube.com/@picklanka', icon: FaYoutube, label: t('footer.social.youtube') },
                                { href: 'https://tiktok.com/@picklanka', icon: FaTiktok, label: t('footer.social.tiktok') },
                            ].map((social) => (
                                <a
                                    key={social.href}
                                    href={social.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-[#009CE6] hover:text-[#96BA33] bg-gray-800/40 rounded-full p-3 hover:bg-gradient-to-r hover:from-[# 009CE6] hover:to-[#96BA33] transition-all duration-300 ease-in-out transform hover:scale-125 focus:outline-none focus:ring-2 focus:ring-[#009CE6]"
                                    aria-label={social.label}
                                >
                                    <social.icon size={28} />
                                </a>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Divider */}
                <hr className="my-10 sm:my-12 lg:my-14 border-gray-500 opacity-50" />

                {/* Footer Bottom */}
                <div className="text-center space-y-4">
                    <p className="text-white text-base sm:text-lg font-medium tracking-wide font-redHatDisplay">
                        © {new Date().getFullYear()} {t('footer.copyright')}
                    </p>
                    <p className="text-white text-sm sm:text-base font-medium font-redHatDisplay">
                        {t('footer.developedBy')}{' '}
                        <a
                            href="https://yourdeveloper.com"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-white hover:text-[#96BA33] transition-all duration-300 ease-in-out hover:underline hover:underline-offset-8 hover:scale-105"
                        >
                            {t('footer.developer')}
                        </a>
                        .
                    </p>
                </div>
            </div>
        </footer>
    );
};

export default Footer;